import { create } from 'zustand';

import {
  listAgents,
  listOfficialAgents,
  listSharedAgents,
} from '@src/services/community';
import type {
  AgentListVO,
  ParamslistAgents,
  ParamslistOfficialAgents,
  ParamslistSharedAgents,
} from '@src/services/community';

/** 合并官方、我创建的、分享给我的列表项，带来源标记 */
export type AgentListItem = AgentListVO & {
  listSource: 'official' | 'created' | 'shared';
  listSources?: Array<'official' | 'created' | 'shared'>;
};

type FetchAgentListResult = {
  official: Awaited<ReturnType<typeof listOfficialAgents>>;
  created: Awaited<ReturnType<typeof listAgents>>;
  shared: Awaited<ReturnType<typeof listSharedAgents>>;
};

interface AgentsState {
  agentList: AgentListItem[];
  listLoading: boolean;
  setAgentList: (agentList: AgentListItem[]) => void;
  setListLoading: (listLoading: boolean) => void;
  /** 并行拉取官方 + 我创建的 + 分享给我的，合并为全量 agentList */
  fetchAgentList: () => Promise<FetchAgentListResult | undefined>;
  /**
   * 从全量 agentList 中筛选：
   * - `'official' | 'created' | 'shared'`：按来源
   * - `string[]`：按 marioAgentId
   */
  pickAgents: (
    filter: 'official' | 'created' | 'shared' | string[],
  ) => AgentListItem[];
}

function normalizeRows(
  rows: AgentListVO[] | undefined,
  listSource: 'official' | 'created' | 'shared',
): AgentListItem[] {
  return (rows ?? []).map((r) => ({ ...r, listSource }));
}

/** 合并多处列表；同一 marioAgentId 优先保留先出现的数据 */
function mergeAgentLists(...lists: AgentListItem[][]): AgentListItem[] {
  const map = new Map<string, AgentListItem>();
  const noIdItems: AgentListItem[] = [];
  for (const list of lists) {
    for (const item of list) {
      const agentId = item.marioAgentId?.trim();
      if (!agentId) {
        noIdItems.push(item);
        continue;
      }
      if (!map.has(agentId)) {
        map.set(agentId, { ...item, listSources: [item.listSource] });
        continue;
      }
      const existed = map.get(agentId)!;
      const nextSources = new Set([
        ...(existed.listSources ?? [existed.listSource]),
        item.listSource,
      ]);
      map.set(agentId, {
        ...existed,
        // 首次出现的数据作为主体，补充多来源标记用于跨 Tab 展示
        listSources: [...nextSources],
      });
    }
  }
  return [...map.values(), ...noIdItems];
}

export const useAgentsStore = create<AgentsState>((set, get) => ({
  agentList: [],
  listLoading: true,
  setAgentList: (agentList) => set({ agentList }),
  setListLoading: (listLoading) => set({ listLoading }),
  fetchAgentList: async () => {
    set({ listLoading: true });
    try {
      const [officialRes, createdRes, sharedRes] = await Promise.all([
        listOfficialAgents({} as ParamslistOfficialAgents),
        listAgents({} as ParamslistAgents),
        listSharedAgents({} as ParamslistSharedAgents),
      ]);
      const officialRows = normalizeRows(
        officialRes?.code === 0 ? officialRes.data : undefined,
        'official',
      );
      const createdRows = normalizeRows(
        createdRes?.code === 0 ? createdRes.data : undefined,
        'created',
      );
      const sharedRows = normalizeRows(
        sharedRes?.code === 0 ? sharedRes.data : undefined,
        'shared',
      );
      set({
        agentList: mergeAgentLists(officialRows, createdRows, sharedRows),
      });
      return { official: officialRes, created: createdRes, shared: sharedRes };
    } finally {
      set({ listLoading: false });
    }
  },
  pickAgents: (filter) => {
    const { agentList } = get();
    if (Array.isArray(filter)) {
      const idSet = new Set(filter.map(String));
      return agentList.filter(
        (a) => Boolean(a.marioAgentId) && idSet.has(a.marioAgentId!),
      );
    }
    return agentList.filter((a) =>
      (a.listSources ?? [a.listSource]).includes(filter),
    );
  },
}));

export default useAgentsStore;
